import { Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '../../../services/data/data.service';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { messages } from '../../funciones/mensajes';
import { TemplateService } from '../../../services/proyecciones/plantilla/template.service';
import Swal from 'sweetalert2';
import { IndicadoresService } from '../../../services/proyecciones/indicadores/indicadores.service';
import { CookieService } from 'ngx-cookie-service';
import { SelectTitleComponent } from '../../../components/select-title/select-title.component';
import { TemplateIndicadorService } from '../../../services/proyecciones/ind-template/template-indicador.service';
import { measureMemory } from 'node:vm';
import { __awaiter } from 'tslib';
import { CuentaContableService } from '../../../services/proyecciones/cuenta-contable/cuenta-contable.service';
import { TypeModifier } from '@angular/compiler';
import { SemaforoService } from '../../../services/proyecciones/semaforos/semaforo.service';

// --- Validador de operaciones aritmeticas ---
function operationSyntaxValidator(control: FormControl) {
  const value = control.value?.trim();
  if (!value) return null;

  // Solo permitir números, operadores y paréntesis
  const validChars = /^[\d+\-*/()\s]+$/;
  if (!validChars.test(value)) {
    return { invalidCharacter: true };
  }

  // Verificar balance de paréntesis
  const stack: string[] = [];
  for (const char of value) {
    if (char === '(') stack.push(char);
    else if (char === ')') {
      if (!stack.pop()) return { unbalancedParentheses: true };
    }
  }
  if (stack.length > 0) return { unbalancedParentheses: true };

  // Verificar secuencias inválidas de operadores
  const invalidPatterns = [
    /\+\s*[\+\/*]/,     // + seguido de otro operador
    /-\s*[\+\/*]/,      // - seguido de otro operador
    /[*\/]\s*[\+\-*/]/, // * o / seguido de otro operador
    /^[*/]/,            // empieza con * o /
    /[+\-*/]$/,         // termina con operador
  ];

  for (const pattern of invalidPatterns) {
    if (pattern.test(value)) return { invalidOperatorSequence: true };
  }

  return null; //sintaxis valida
}

@Component({
  selector: 'app-config-indicadores',
  templateUrl: './config-indicadores.component.html',
  styleUrl: './config-indicadores.component.css'
})
export class ConfigIndicadoresComponent {
  @ViewChild(SelectTitleComponent) titTemp!: SelectTitleComponent;
  public plantilla;
  private  templateId;
  public dataPlantillas = [];

  public indicadores = [];
  public cuentas = [];
  public grupos = [];
  public editIndicador = {grupo: '', indicador: '', descrip: ''}
  public formIndicador = new FormGroup({
    grupo: new FormControl('', [Validators.required]),
    indicador: new FormControl('', [Validators.required]),
    descripcion: new FormControl('', [Validators.required]),
    numerador: new FormControl('', [Validators.required, operationSyntaxValidator]),
    cuentaNum: new FormControl('', [Validators.required]),
    denominador: new FormControl('', [Validators.required, operationSyntaxValidator]),
    cuentaDen: new FormControl('', [Validators.required]),
    semaforos: new FormArray([]),
    semaforoid: new FormControl(''),
    limiteinferior: new FormControl(''),
    limitesuperior: new FormControl(''),
  })

  public selectAll = false;
  public universo = false;
  public nuevoInd = true;
  public indicadorSelect;
  public tituloModal;
  public numSemaforosSeleccionado : number | null = null;

  constructor(
    private cookies: CookieService,
    public dataService: DataService,
    private translate: TranslateService,
    private plantillas: TemplateService,
    private indicador: IndicadoresService,
    private tmpIndicador: TemplateIndicadorService,
    private cuentaContable: CuentaContableService,
    private semaforo: SemaforoService,
  ){
    this.templateId = Number(cookies.get('templateId'));
    this.getGrupos();
    this.getPlantillas();
  }
  private async getPlantillas(){
    try{      
      const res = await firstValueFrom(this.plantillas.getTemplates());
      for(const d of res){
        this.dataPlantillas.push({
          id: d.templateid,
          nombre: d.nombre,
          descripcion: d.descripcion,
          institutionid: d.instituciones.length !== 0 ? d.instituciones[0].institutionid : null,
          nombreInst:d.instituciones.length !== 0 ? d.instituciones[0].alias : '',
          clavepais: d.instituciones.length !== 0 ? d.instituciones[0].clavepais : null,
          activo: d.instituciones.length !== 0 ? d.instituciones[0].activo_relacion : null,
        })
      }
      if(this.templateId !== 0){
        this.plantilla = this.dataPlantillas.filter(t => t.id === Number(this.templateId))[0];
        this.titTemp.selected = this.plantilla;
        this.selectPlantilla(this.plantilla)
      }
    }catch(er){
      messages.errorMessageGraph(er);
    }
  }
  selectPlantilla(d) {
    this.plantilla = d;
    this.cookies.set('templateId', this.plantilla.id, {path: '/'}); 
    this.templateId = this.plantilla.id;
    this.indicadores = [];
    this.getIndicadores();
    this.getCuentas();
  }
  private async getIndicadores(){
    try{
      const res = await firstValueFrom(this.indicador.getIndicadorTmp(this.templateId));
      this.indicadores = res;
      this.universo = false;
    }catch(e){
      this.getIndicadoresAll();
    }
  }
  private async getCuentas(){
    try{
      const res = await firstValueFrom(this.cuentaContable.getCuentas(this.templateId));
      this.cuentas = res;
    }catch(e){
      messages.errorMessageGraph(e.error);
    }
  }
  private async getIndicadoresAll(){
    try{
      const res = await firstValueFrom(this.indicador.getIndicadoresAll(this.plantilla.clavepais));
      this.universo = true;
      for(const d of res){
        this.indicadores.push({
          grupo: this.grupos.filter(i => i.grupoid == d.grupoid)[0].nombre,
          grupoid: d.grupoid,
          indicador: d.indicador,
          descripcion: d.descripcion,
          indicadorid: d.indicadorid,
          selected: false,
        })
      }
    }catch(e){
      messages.errorMessageGraph(this.translate.instant('proyFinanciera.configIndicador.noIndPais'));
    }
  }
  private async getGrupos(){
    try{
      const res = await firstValueFrom(this.indicador.getGrupos());
      this.grupos = res;
    }catch(e){
      messages.errorMessageGraph(e.error);
    }
  }
  public getGrupoNombre(id){
    return this.grupos.filter(i => i.grupoid == id)[0].nombre;
  }
  public seleccionarTodos(){
    this.indicadores.forEach(i => i.selected = this.selectAll);
  }
  public checkSeleccionados(){
    this.selectAll = this.indicadores.every(i => i.selected);
  }
  public async vincularIndicadores(){
    try{
      const seleccionados = this.indicadores.filter( i => i.selected);
      if(seleccionados.length > 0){
        const result = await messages.confirmationSave(
          this.translate.instant('proyFinanciera.configIndicador.infoCorr'),
          this.translate.instant('acciones.continuar'),
          this.translate
        );
        if(result){
          messages.messageSaving(this.translate.instant('swal.agregandoInfo'), this.translate.instant('swal.espere'));
          for(const d of seleccionados){
            const res = await firstValueFrom(this.tmpIndicador.setTempIndSF(this.plantilla.id, d.indicadorid));
          }
          const res = await firstValueFrom(this.indicador.getIndicadorTmp(this.plantilla.id));
          this.indicadores = res;
          this.universo = false;
          messages.succesMessageGraph(this.translate.instant('swal.infoGuardado'));
        }
      }      
    }catch(e){
      messages.errorMessageGraph(e.error)
    }
  }
  public clickFuera(event, modal) {
    if (event.target === document.getElementById(modal)) {
      document.getElementById(modal).classList.add('moveShake');
      setTimeout(() => {
        document.getElementById(modal).classList.remove('moveShake');
      }, 600);
    }
  }

  public modalIndicador(){
    this.tituloModal = this.translate.instant('proyFinanciera.configIndicador.indicador');
    this.formIndicador.reset();
    this.semaforos.clear();
    this.numSemaforosSeleccionado = null;
    this.formIndicador.get('grupo')?.enable();
    this.formIndicador.get('indicador')?.enable();
    this.formIndicador.get('descripcion')?.enable();
    this.nuevoInd = true;
    document.getElementById('indicador').classList.add("show");
  }
  public closeModal() {
    this.indicadorSelect = undefined;
    document.getElementById('indicador').classList.remove("show");
  }
  private cuentasValidas(){
    var codigos = this.formIndicador.get('numerador')?.value || '' 
    codigos += '+'+ this.formIndicador.get('denominador')?.value || '';
    const cod = codigos.match(/\d+/g) || [];
    const codigosCatalogo = this.cuentas.map(c => c.codigo);
    const codigosInvalidos = cod.filter(c => !codigosCatalogo.includes(c));
    if (codigosInvalidos.length > 0) {
      this.formIndicador.get('numerador')?.setErrors({ codigoInvalido: codigosInvalidos });
      return false;
    }
    this.formIndicador.get('numerador')?.setErrors(null);
    return true;
  }
  public numeroSemaforos(num){
    const actuales = this.semaforos.length;
    if(num > actuales){
      for(let i = actuales; i < num; i++){
        this.semaforos.push(this.crearSemaforo());
      }
    }else{
      for (let i = actuales; i > num; i--) {
        this.semaforos.removeAt(i - 1);
      }
    } 
  }
  get semaforos(): FormArray {
    return this.formIndicador.get('semaforos') as FormArray;
  }
  private crearSemaforo(): FormGroup {
    return new FormGroup({
      color: new FormControl('#00ff00', [Validators.required]),
      condicion1_operador: new FormControl('<', Validators.required),
      condicion1_valor: new FormControl('', Validators.required),
      operador_logico: new FormControl(''),
      condicion2_operador: new FormControl(''),
      condicion2_valor: new FormControl('')
    });
  }
  public async guardarIndicador(){
    if(this.cuentasValidas()){
      try{
        messages.messageSaving(this.translate.instant('swal.agregandoInfo'), this.translate.instant('swal.espere'));
        const form = {
          numerador: {
            codigo: this.formIndicador.controls['numerador'].value,
            cuenta: this.formIndicador.controls['cuentaNum'].value
          },
          denominador: {
            codigo: this.formIndicador.controls['denominador'].value,
            cuenta: this.formIndicador.controls['cuentaDen'].value
          },
        }
        const semaforos = [];
        for(const s of this.formIndicador.controls['semaforos'].value){
          const reglas = [];
          switch(s.operador_logico){
            case "":
              reglas.push({
                rangomin: null,
                rangomax: s.condicion1_valor,
                operadores: [null,s.condicion1_operador]
              });
              break;
            case "AND":
              reglas.push({
                rangomin: s.condicion1_valor,
                rangomax: s.condicion2_valor,
                operadores: [s.condicion1_operador,s.condicion2_operador]
              });
              break;
            case "OR":
              reglas.push({
                rangomin: null,
                rangomax: s.condicion1_valor,
                operadores: [null,s.condicion1_operador]
              });
              reglas.push({
                rangomin: null,
                rangomax: s.condicion2_valor,
                operadores: [null,s.condicion2_operador]
              });
              break;

          }
          semaforos.push({
            color: s.color,
            reglas,              
          })
        }
        const sem = {
          limiteinferior: Number(this.formIndicador.controls['limiteinferior'].value),
          limitesuperior: Number(this.formIndicador.controls['limitesuperior'].value),
          condiciones: semaforos,
        }
        if(this.indicadorSelect === undefined){// se crea un nuevo indicador para la plantilla 
          const ind = {
            grupoid: this.formIndicador.controls['grupo'].value['grupoid'],
            clavepais: this.plantilla.clavepais,
            indicador: this.formIndicador.controls['indicador'].value,
            descripcion: this.formIndicador.controls['descripcion'].value,
            formula : form,
            templateid: this.plantilla.id,
            indicadorid: null,
          }
          const res = await firstValueFrom(this.indicador.postIndicadorTemplate(ind))
          const res2 = await firstValueFrom(this.semaforo.postSemaforo(res.indicadorid, this.plantilla.id, sem));
          ind.indicadorid = res.indicadorid;
          this.indicadores.push(ind)
          messages.succesMessageGraph(this.translate.instant('swal.infoGuardado'));
        }else{// se configura un indicador con formula y semaforos
          const formula = {
            templateid: this.plantilla.id,
            indicadorid: this.indicadorSelect.indicadorid,
            formula:form
          }
          const res = await firstValueFrom(this.indicador.postFormula(formula));
          if(this.formIndicador.controls['semaforoid'].value !== null){//ya existia un semaforo y se va a actualizar
            var semId = Number(this.formIndicador.controls['semaforoid'].value);
            const res = await firstValueFrom(this.semaforo.patchSemaforo(semId, this.plantilla.id, sem))
          }else{// se crea un nuevo semaforo
            const res2 = await firstValueFrom(this.semaforo.postSemaforo(this.indicadorSelect.indicadorid, this.plantilla.id, sem));
          }
          messages.succesMessageGraph(this.translate.instant('swal.swalGuardo'));
        }
        this.closeModal();
      }catch(e){
        messages.errorMessageGraph(e.error);
      }
    }
  }
  public async configurarIndicador(ind){     
    this.formIndicador.reset();
    this.semaforos.clear();
    this.numSemaforosSeleccionado = null;
    var aux = ind;
    if(typeof ind.formula.numerador !== 'object' && ind.formula.numerador !== null){
      aux.formula.denominador = {cuenta: ind.formula.denominador.cuenta, codigo: ind.formula.denominador.codigo}
      aux.formula.numerador = {cuenta: ind.formula.numerador.cuenta, codigo: ind.formula.numerador.codigo}
    }
    this.indicadorSelect = aux;
    this.tituloModal = `${this.translate.instant('proyFinanciera.configIndicador.indicador')}: ${this.indicadorSelect.indicador}`;
    const form = await this.getFormulaIndicadorTemplate();
    if(form.length > 0 && form[0].formula !== null){
      this.formIndicador.patchValue({
        grupo: this.grupos.filter(i => i.grupoid === this.indicadorSelect.grupoid)[0],
        indicador: this.indicadorSelect.indicador,
        descripcion: this.indicadorSelect.descripcion,
        cuentaNum: form[0].formula.numerador.cuenta,
        cuentaDen: form[0].formula.denominador.cuenta,
        numerador: form[0].formula.numerador.codigo,
        denominador: form[0].formula.denominador.codigo,
      })
    }else{
      this.formIndicador.patchValue({
        grupo: this.grupos.filter(i => i.grupoid === this.indicadorSelect.grupoid)[0],
        indicador: this.indicadorSelect.indicador,
        descripcion: this.indicadorSelect.descripcion,
        cuentaNum: this.indicadorSelect.formula.numerador.cuenta,
        cuentaDen: this.indicadorSelect.formula.denominador.cuenta,
        numerador: this.indicadorSelect.formula.denominador.codigo,
        denominador: this.indicadorSelect.formula.denominador.codigo,
      })
    }
    var semaforo = await this.getSemaforos(this.indicadorSelect.indicadorid);        
    if(semaforo.length > 0) this.mapReglasToForm(semaforo);
    this.formIndicador.get('grupo')?.disable();
    this.formIndicador.get('indicador')?.disable();
    this.formIndicador.get('descripcion')?.disable();
    this.nuevoInd = false;
    document.getElementById('indicador').classList.add("show");
  }
  private async getSemaforos(id){
    try{
      const res = await firstValueFrom(this.semaforo.getSemaforos(id, this.plantilla.id));
      var semInd = res.filter(i=> i.indicadorid === this.indicadorSelect.indicadorid)
      return semInd;
    }catch(e){
      return [];
    }
  }
  private mapReglasToForm(semaforos: any[]) {
    const semaforosArray = this.formIndicador.get('semaforos') as FormArray;
    semaforosArray.clear();
    this.formIndicador.patchValue({
      limiteinferior: semaforos[0].limiteinferior,
      limitesuperior: semaforos[0].limitesuperior,
      semaforoid: semaforos[0].semaforoid,
    });
    this.numSemaforosSeleccionado = semaforos[0].condiciones.length;
    for (const c of semaforos[0].condiciones) {
      const reglas = c.reglas;
      let formConfig: any = {
        color: c.color,
        operador_logico: "",
        condicion1_operador: null,
        condicion1_valor: null,
        condicion2_operador: null,
        condicion2_valor: null
      };

      // Caso 1: una sola regla → puede ser "" o AND
      if (reglas.length === 1) {
        const r = reglas[0];

        // Si tiene ambos operadores → es "AND"
        if (r.operadores[0] !== null && r.operadores[1] !== null) {
          formConfig.operador_logico = "AND";
          formConfig.condicion1_operador = r.operadores[0];
          formConfig.condicion1_valor = r.rangomin;
          formConfig.condicion2_operador = r.operadores[1];
          formConfig.condicion2_valor = r.rangomax;
        }

        // Si solo tiene el operador derecho → es ""
        else {
          formConfig.operador_logico = "";
          formConfig.condicion1_operador = r.operadores[1];
          formConfig.condicion1_valor = r.rangomax;
        }
      }

      // Caso 2: dos reglas → es OR
      else if (reglas.length === 2) {
        formConfig.operador_logico = "OR";

        const r1 = reglas[0];
        const r2 = reglas[1];

        formConfig.condicion1_operador = r1.operadores[1];
        formConfig.condicion1_valor = r1.rangomax;

        formConfig.condicion2_operador = r2.operadores[1];
        formConfig.condicion2_valor = r2.rangomax;
      }

      // Agregar al FormArray
      semaforosArray.push(new FormGroup({
        color: new FormControl(formConfig.color),
        operador_logico: new FormControl(formConfig.operador_logico),
        condicion1_operador: new FormControl(formConfig.condicion1_operador),
        condicion1_valor: new FormControl(formConfig.condicion1_valor),
        condicion2_operador: new FormControl(formConfig.condicion2_operador),
        condicion2_valor: new FormControl(formConfig.condicion2_valor)
      }));
    }
  }


  private async getFormulaIndicadorTemplate(){
    try{
      const res = await firstValueFrom(this.tmpIndicador.getFormulaIndTmp(this.indicadorSelect.indicadorid, this.plantilla.id));
      return res;
    }catch(e){
      return [];
    }
  }
  public nuevoGrupo(){
    Swal.fire({
      title: this.translate.instant('proyFinanciera.configIndicador.nombreGrupo'),
      input: 'text',
      inputPlaceholder: this.translate.instant('proyFinanciera.configIndicador.ttnombre'),
      confirmButtonColor: '#ff6b00',
      cancelButtonColor: '#B9B9BC',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('acciones.guard'),
      cancelButtonText: this.translate.instant('acciones.cancel'),
      reverseButtons: true,
      customClass: {
        actions: 'aleema-swal-actions',
        confirmButton: 'aleema-swal-confirm',
        cancelButton: 'aleema-swal-cancel',
        input: 'aleema-swal-select'
      },
      inputValidator: (value) => {
        if (!value) {
          return this.translate.instant('proyFinanciera.configIndicador.errorNombre');
        }
        return null;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try{
          const nombre = result.value;
          const res = await firstValueFrom(this.indicador.postGrupo(nombre));
          this.grupos.push({nombre: res.nombre, id:res.grupoid});
          messages.succesMessageGraph(this.translate.instant('swal.infoGuardado'));
        }catch(e){
          messages.errorMessageGraph(e.error.error);
        }
      }
    });
  }

}
